import React, { useState, useEffect, useContext } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import TenantConfig from '../../config';

/** context */
import { UserContext } from '../../context/UserContext';
import { formatUSDCurrency } from '../../helpers/format';

/** service */
import { updateApplication, validateProperty } from '../../service/api';

/** components */
import { AddInsuranceModal, AddLicenseModal } from '../FormElements';
import { ScrollToTop } from '../ScrollToTop';

const License = ({ setError, handleSkipToNextSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = new URLSearchParams(location.search).get('edit');

  const { appData, updateAppData, setLoading } = useContext(UserContext);
  const licenseFeature = appData?.applicationFeatures.find(
    (feature) => feature.itemType === 'LicenseProperty'
  );

  const [data, setData] = useState(licenseFeature?.value);
  const [dataError, setDataError] = useState({});
  const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false);
  const [showAddLicenseModal, setShowAddLicenseModal] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [editInsuranceIndex, setEditInsuranceIndex] = useState(-1);
  const [editLicenseIndex, setEditLicenseIndex] = useState(-1);

  useEffect(() => {
    ReactTooltip.rebuild();
    console.log(licenseFeature?.value, licenseFeature?.value?.licenses);
    setLicenses(licenseFeature?.value?.licenses ? licenseFeature?.value?.licenses : []);
    setInsurances(licenseFeature?.value?.insurances ? licenseFeature?.value?.insurances : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseFeature]);

  if (!licenseFeature) {
    return;
  }

  const handleSubmit = async (goToDashboard) => {
    setLoading(true);
    setDataError({});

    // when frontend validation is passed
    try {
      // update applicant instance
      const updatedData = await updateApplication([
        {
          itemType: licenseFeature.itemType,
          dataType: licenseFeature.dataType,
          name: licenseFeature.name,
          value: {
            ...data,
            insurances: insurances,
            licenses: licenses,
          },
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      // backend validation
      const validationData = await validateProperty(licenseFeature.name);
      setDataError({});

      if (validationData.data && validationData.data.errors) {
        if (validationData.data.errors.length) {
          validationData.data.errors.map((error) => {
            const attributes = error.property.split('.');
            setDataError((dataError) => ({
              ...dataError,
              [attributes[1]]: dataError[attributes[1]]
                ? dataError[attributes[1]].concat('. ', error.message)
                : error.message,
            }));
          });
          setLoading(false);
          return false;
        }
      }
    } catch (error) {
      if (error?.response?.data?.errors && error.response.data.errors.length) {
        setError(error.response.data.errors[0].message);
      }
      setLoading(false);
      return;
    }

    // continue next step
    setLoading(false);
    if (isEdit) {
      return navigate('/review');
    } else if (goToDashboard) {
      return navigate('/dashboard');
    }
    handleSkipToNextSection();
  };

  const handleEditInsurance = (editIndex) => {
    setEditInsuranceIndex(editIndex);
    setShowAddInsuranceModal(true);
  };

  const handleDeleteInsurance = (delIndex) => {
    setInsurances(
      insurances.filter((insurance, index) => {
        return index !== delIndex;
      })
    );
  };

  const handleSaveInsurance = (insuranceData) => {
    if (editInsuranceIndex == -1) {
      setInsurances([...insurances, insuranceData]);
    } else {
      setInsurances(
        insurances.map((insurance, index) => {
          if (editInsuranceIndex == index) {
            return insuranceData;
          }
          return insurance;
        })
      );
      setEditInsuranceIndex(-1);
    }
  };

  const handleEditLicense = (editIndex) => {
    setEditLicenseIndex(editIndex);
    setShowAddLicenseModal(true);
  };

  const handleDeleteLicense = (delIndex) => {
    setLicenses(
      licenses.filter((license, index) => {
        return index !== delIndex;
      })
    );
  };

  const handleSaveLicense = (licenseData) => {
    if (editLicenseIndex == -1) {
      setLicenses([...licenses, licenseData]);
    } else {
      setLicenses(
        licenses.map((license, index) => {
          if (editLicenseIndex == index) {
            return licenseData;
          }
          return license;
        })
      );
      setEditLicenseIndex(-1);
    }
  };

  return (
    <ScrollToTop>
      <div className="license">
        <div className="d-flex pr-btn-style">
          <h3 className="insurance-text">
            Please provide all business, contractor, trade, professional licenses required for your
            Project.
          </h3>
          <button
            className="add-btn primary-btn"
            onClick={() => {
              setShowAddLicenseModal(true);
              setEditLicenseIndex(-1);
            }}
          >
            Add License
          </button>
        </div>
        {licenses && licenses.length > 0 ? (
          <div className="content-part">
            <table className="license-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>State</th>
                  <th>Number</th>
                  <th>Expiration Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {licenses?.map((license, index) => {
                  return (
                    <tr key={'license' + index}>
                      <td>{license.type}</td>
                      <td>{license.state}</td>
                      <td>{license.number}</td>
                      <td>{license.expirationDate}</td>
                      <td>
                        {' '}
                        <button onClick={() => handleEditLicense(index)}>Edit</button>
                        <button onClick={() => handleDeleteLicense(index)}>Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="content-part">
            <p className="empty-text">You do not have any license added</p>
          </div>
        )}
        <div className="d-flex pr-btn-style insurance-part">
          <span className="insurance-text">
            Please provide all business insurance required for your Project.
          </span>
          <button
            className="add-btn primary-btn"
            onClick={() => {
              setShowAddInsuranceModal(true);
              setEditInsuranceIndex(-1);
            }}
          >
            Add Insurance
          </button>
        </div>
        <div className="content-part">
          {insurances && insurances.length > 0 ? (
            <table className="insurance-table">
              <thead>
                <tr>
                  <th>Insurance Provider</th>
                  <th>Policy Number</th>
                  <th className="amount">Coverage Amount</th>
                  <th>Expiration Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {insurances?.map((insurance, index) => {
                  return (
                    <tr key={'insurance' + index}>
                      <td>{insurance.provider}</td>
                      <td>{insurance.policyNumber}</td>
                      <td className="amount">{formatUSDCurrency(insurance.coverageAmount)}</td>
                      <td>{insurance.expirationDate}</td>
                      <td>
                        {' '}
                        <button onClick={() => handleEditInsurance(index)}>Edit</button>
                        <button onClick={() => handleDeleteInsurance(index)}>Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="empty-text">You do not have any insurance added</p>
          )}
        </div>

        <div className="d-flex bottom-actions align-items-center justify-content-around">
          <div className="actions d-flex">
            {!!TenantConfig?.settings?.isSaveAndContinueButtonEnabled ? (
              <button onClick={() => handleSubmit(true)} className="primary-btn">
                Save and Continue Later
              </button>
            ) : (
              ''
            )}
            {
              <button
                className="primary-btn"
                disabled={!insurances?.length > 0 || !licenses?.length > 0}
                onClick={() => handleSubmit(false)}
              >
                {isEdit ? 'Save and Return to Application Summary' : 'Continue'}
              </button>
            }
          </div>
        </div>
        <AddInsuranceModal
          showModal={showAddInsuranceModal}
          setShowModal={setShowAddInsuranceModal}
          handleSave={handleSaveInsurance}
          insurance={editInsuranceIndex != -1 ? insurances[editInsuranceIndex] : {}}
          isEdit={editInsuranceIndex != -1}
        ></AddInsuranceModal>

        <AddLicenseModal
          showModal={showAddLicenseModal}
          setShowModal={setShowAddLicenseModal}
          handleSave={handleSaveLicense}
          license={editLicenseIndex != -1 ? licenses[editLicenseIndex] : {}}
          isEdit={editLicenseIndex != -1}
          licenseFeature={licenseFeature}
        ></AddLicenseModal>
      </div>
    </ScrollToTop>
  );
};

export default License;
